import React, { useState, useContext } from 'react';
import { AppContext } from '../App';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { 
  Mail, Send, MessageSquare, CheckCircle, 
  AlertCircle, ExternalLink 
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ContactUs() {
  const { user } = useContext(AppContext);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!subject.trim() || !message.trim()) {
      toast.error('Please fill in all fields');
      return;
    }
    
    try {
      setSending(true);
      await axios.post(
        `${API}/contact`,
        { subject, message },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        }
      );
      
      setSent(true);
      toast.success('Message sent successfully!');
      setSubject('');
      setMessage('');
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error(error.response?.data?.detail || 'Failed to send message. Please try emailing us directly.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-stone-100 to-stone-50 py-8 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-4">
              <MessageSquare className="w-8 h-8 text-amber-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-800">Contact Us</h1>
            <p className="text-slate-600 mt-2">
              Have a question or feedback? We'd love to hear from you!
            </p>
          </div>

          {/* Contact Form Card */}
          <Card className="shadow-lg border-0 mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-slate-800">
                <Send className="w-5 h-5 text-amber-600" />
                Send us a message
              </CardTitle>
              <CardDescription>
                Fill out the form below and we'll get back to you as soon as possible.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {sent ? (
                <div className="text-center py-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-800 mb-2">Message Sent!</h3>
                  <p className="text-slate-600 mb-4">
                    Thanks for reaching out. We'll get back to you soon.
                  </p>
                  <Button 
                    onClick={() => setSent(false)}
                    variant="outline"
                    className="border-amber-500 text-amber-600 hover:bg-amber-50"
                  >
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="email" className="text-slate-700">Your Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={user?.email || ''}
                      disabled
                      className="bg-slate-50 text-slate-600"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      We'll respond to this email address
                    </p>
                  </div>
                  
                  <div>
                    <Label htmlFor="subject" className="text-slate-700">Subject</Label>
                    <Input
                      id="subject"
                      type="text"
                      placeholder="What's this about?"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      className="border-slate-300 focus:border-amber-500"
                      data-testid="input-subject"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="message" className="text-slate-700">Message</Label>
                    <Textarea
                      id="message"
                      placeholder="Tell us what's on your mind..."
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      className="border-slate-300 focus:border-amber-500 min-h-[150px]"
                      data-testid="input-message"
                      required
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    disabled={sending}
                    className="w-full bg-amber-500 hover:bg-amber-600 text-white"
                    data-testid="btn-send"
                  >
                    {sending ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>

          {/* Direct Email Card */}
          <Card className="bg-slate-50 border-slate-200">
            <CardContent className="py-6">
              <div className="flex items-center gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                    <Mail className="w-6 h-6 text-amber-600" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-800">Prefer to email directly?</h3>
                  <p className="text-slate-600 text-sm">
                    You can reach us anytime at:
                  </p>
                  <a 
                    href="mailto:support@survivorfantasy.live"
                    className="inline-flex items-center gap-1 text-amber-600 hover:text-amber-700 font-medium mt-1"
                  >
                    support@survivorfantasy.live
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
